/**
 * @congress/auth-helper v1.0.0
 * @version 1.0.0
 * @license CYIM: www.cyim.com
 */

import Keycloak from 'keycloak-js';
import AuthType from './AuthType.enum.js';
const state = {
  NOINIT: "noinit",
  INIT: "init",
  INITIED: "initied"
};
const eventAuthType = {
  ON_AUTH_SUCCESS: "onAuthSuccess",
  ON_AUTH_ERROR: "onAuthError",
  ON_AUTH_REFRESH_SUCCESS: "onAuthRefreshSuccess",
  ON_AUTH_REFRESH_ERROR: "onAuthRefreshError",
  ON_LOGOUT: "onAuthLogout",
  ON_TOKEN_EXPIRED: "onTokenExpired"
};

// keycloak-js doc: https://github.com/keycloak/keycloak-documentation/blob/master/securing_apps/topics/oidc/javascript-adapter.adoc

class KeycloakService {
  /**
   * Use to init keycloak
   * @type {boolean}
   * @private
   */
  _init = (() => state.NOINIT)();
  _callbacks = (() => ({
    [eventAuthType.ON_AUTH_SUCCESS]: [],
    [eventAuthType.ON_AUTH_ERROR]: [],
    [eventAuthType.ON_AUTH_REFRESH_SUCCESS]: [],
    [eventAuthType.ON_AUTH_REFRESH_ERROR]: [],
    [eventAuthType.ON_LOGOUT]: [],
    [eventAuthType.ON_TOKEN_EXPIRED]: []
  }))();
  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Default constructor
   * @constructor KeycloakService
   * @param {Object} initOptions  Options to use Keycloak
   * @param {boolean} initOptions.autoLogin - if true, a login is triggered during initialization or a getToken. Otherwise you have to call the login function and check the given token. (default is true).
   * @param {boolean} initOptions.useNonce - Adds a cryptographic nonce to verify that the authentication response matches the request (default is true).
   * @param {string} initOptions.onLoad - Specifies an action to do on load. Supported values are login-required or check-sso.
   * @param {string} initOptions.silentCheckSsoRedirectUri - Set the redirect uri for silent authentication check if onLoad is set to 'check-sso'.
   * @param {boolean} initOptions.silentCheckSsoFallback - Enables fall back to regular check-sso when silent check-sso is not supported by the browser (default is true).
   * @param {string} initOptions.token - Set an initial value for the token.
   * @param {string} initOptions.refreshToken - Set an initial value for the refresh token.
   * @param {string} initOptions.idToken - Set an initial value for the id token (only together with token or refreshToken).
   * @param {number} initOptions.timeSkew - Set an initial value for skew between local time and {project_name} server in seconds (only together with token or refreshToken).
   * @param {boolean} initOptions.checkLoginIframe - Set to enable/disable monitoring login state (default is true).
   * @param {number} initOptions.checkLoginIframeInterval - Set the interval to check login state (default is 5 seconds).
   * @param {("fragment"|"query")} initOptions.responseMode - Set the OpenID Connect response mode send to {project_name} server at login request. Valid values are query or fragment. Default value is fragment, which means that after successful authentication will {project_name} redirect to JavaScript application with OpenID Connect parameters added in URL fragment. This is generally safer and recommended over query.
   * @param {string} initOptions.flow - Set the OpenID Connect flow. Valid values are standard, implicit or hybrid.
   * @param {boolean} initOptions.enableLogging - Enables logging messages from Keycloak to the console (default is false).
   * @param {string} initOptions.pkceMethod - The method for Proof Key Code Exchange (PKCE) to use. Configuring this value enables the PKCE mechanism. Available options:
   * "S256" - The SHA256 based PKCE method
   * @param {number} initOptions.messageReceiveTimeout - Set a timeout in milliseconds for waiting for message responses from the Keycloak server. This is used, for example, when waiting for a message during 3rd party cookies check. The default value is 10000.
   * @returns {KeycloakService}
   */
  constructor(initOptions) {
    if (!KeycloakService.instance) {
      KeycloakService.instance = this;
      this.type = AuthType.KEYCLOAK;
      this.keycloakOpt = Object.assign({
        clientId: "apps-congres",
        url: "http://keycloak.staging-k8s.groupe.zzroot.com/auth",
        realm: "master"
      }, initOptions);
      this.keycloakInstance = new Keycloak(this.keycloakOpt);
      if (process.env.NODE_ENV == "development") {
        this.keycloakInstance.onAuthSuccess = () => {
          console.log('Auth Success');
          KeycloakService.instance.callCallBack(eventAuthType.ON_AUTH_SUCCESS);
        };
        this.keycloakInstance.onAuthError = errorData => {
          console.log("Auth Error: " + JSON.stringify(errorData));
          KeycloakService.instance.callCallBack(eventAuthType.ON_AUTH_ERROR);
        };
        this.keycloakInstance.onAuthRefreshSuccess = () => {
          console.log('Auth Refresh Success');
          KeycloakService.instance.callCallBack(eventAuthType.ON_AUTH_REFRESH_SUCCESS);
        };
        this.keycloakInstance.onAuthRefreshError = () => {
          console.log('Auth Refresh Error');
          KeycloakService.instance.callCallBack(eventAuthType.ON_AUTH_REFRESH_ERROR);
        };
        this.keycloakInstance.onAuthLogout = () => {
          console.log('Auth Logout');
          KeycloakService.instance.callCallBack(eventAuthType.ON_LOGOUT);
        };
        this.keycloakInstance.onTokenExpired = () => {
          console.log('Access token expired.');
          this.updateToken(); // renew automatically
          KeycloakService.instance.callCallBack(eventAuthType.ON_TOKEN_EXPIRED);
        };
      }
    }
    return KeycloakService.instance;
  }
  callCallBack(callbackKey) {
    this._callbacks[callbackKey].forEach(cb => {
      try {
        cb();
      } catch (e) {
        console.log("Failed to execute callback", e);
      }
    });
  }

  /**
   * Register callback for each authentication events
   * @param {eventAuthType} callbackKey 
   * @param {function} callback 
   * @returns boolean to indicate if register succeded
   */
  registerCallback(callbackKey, callback) {
    if (callbackKey in this._callbacks) {
      this._callbacks[callbackKey].push(callback);
      return true;
    }
    return false;
  }

  /**
   * Unregister callback for authentication event
   * @param {eventAuthType} callbackKey 
   * @param {function} callback
   * @returns boolean to indicate if unregister succeded 
   */
  unregisterCallback(callbackKey, callback) {
    const index = this._callbacks[callbackKey].indexOf(callback);
    if (index >= 0) {
      this._callbacks[callbackKey].splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Called to initialize the adapter.
   * @returns {Keycloak.KeycloakPromise<boolean, Keycloak.KeycloakError>}
   */
  async init() {
    try {
      if (this._init === state.NOINIT) {
        this._init = state.INIT;
        let authenticated = await this.keycloakInstance.init(this.keycloakOpt);
        if (!authenticated && this.keycloakOpt.autoLogin) {
          this.login();
        }
        this._init = state.INITIED;
      } else if (this._init === state.INIT) {
        while (this._init !== state.INITIED) {
          await this._sleep(100);
        }
      }
    } catch (e) {
      this._init === state.NOINIT;
      console.log("Failed to init keycloack connection", e);
    }
  }

  /**
   * Get instance of KeycloakService
   * @returns {Keycloak | Keycloak.KeycloakInstance}
   */
  getInstance() {
    return this.keycloakInstance;
  }

  /**
   * Redirects to login form on (options is an optional object with redirectUri and/or prompt fields).
   * @returns {Keycloak.KeycloakPromise<void, void>}
   */
  async login() {
    if (!this._init === state.NOINIT) {
      await this.init();
    }
    return this.keycloakInstance.login();
  }
  async _checkToken() {
    await this.init();
    if (!this.keycloakInstance.token && this.keycloakOpt.autoLogin) {
      this.login();
    } else if (this.isTokenExpired()) {
      await this.updateToken();
    }
  }

  /**
   * Is true if the user is authenticated, false otherwise.
   * @returns {boolean}
   */
  async isAuthenticated() {
    await this._checkToken();
    return this.keycloakInstance.authenticated || false;
  }

  /**
   * The base64 encoded token that can be sent in the Authorization header in requests to services.
   * @returns {string | Promise<string>}
   */
  async getToken() {
    await this._checkToken();
    return this.keycloakInstance.token;
  }

  /**
   * The parsed token as a JavaScript object.
   * @returns {Keycloak.KeycloakTokenParsed}
   */
  async getTokenParsed() {
    await this._checkToken();
    return this.keycloakInstance.tokenParsed;
  }

  /**
   * The base64 encoded ID token.
   * @returns {string}
   */
  getIdToken() {
    return this.keycloakInstance.idToken;
  }

  /**
   * The base64 encoded refresh token that can be used to retrieve a new token.
   * @returns {string}
   */
  getRefreshToken() {
    return this.keycloakInstance.refreshToken;
  }

  /**
   * Returns true if the token has less than minValidity seconds left before it expires (minValidity is optional, if not specified 0 is used).
   * @param {number} minValidity - duration in seconds
   * @returns {boolean}
   */
  isTokenExpired() {
    let minValidity = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    let isExpired;
    try {
      isExpired = this.keycloakInstance.isTokenExpired(minValidity);
    } catch (e) {
      isExpired = false;
    }
    return isExpired;
  }

  /**
   * If the token expires within minValidity seconds (minValidity is optional, if not specified 300 is used) the token is refreshed. If the session status iframe is enabled, the session status is also checked.
   * @param {number} minValidity - (default: 300s(5min))
   * @returns {Keycloak.KeycloakPromise<boolean, boolean>}
   */
  async updateToken() {
    let minValidity = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 300;
    try {
      await this.keycloakInstance.updateToken(minValidity);
    } catch (e) {
      console.error('Failed to update token', e);
    }
  }

  /**
   * Redirects to logout.
   * @param options Logout options.
   * @returns {Keycloak.KeycloakPromise<void, void>}
   */
  logout(options) {
    return this.keycloakInstance.logout(options);
  }
}
export { KeycloakService as default };
//# sourceMappingURL=KeycloakService.js.map
